import { DEFAULT_FORM_INPUT_TYPES, ITEM_STATUS_CODES } from '../../utils/constants';
import { formatOperatorResponseLabel, shouldCreateIssueForPrompt } from '../../utils/formPromptMapping';
import { isReportUMCounterPromptLabel } from '../../utils/reportUtils';
import type { tItems } from '../../types';

/** Saved Daily Service lines keep autoFlag even though the report row type does not list it. */
type ReportLine = tItems & { autoFlag?: boolean };

/**
 * An unscheduled item that fired (quantity entered, Not Added, and so on) is a caution.
 * The saved answer is the quantity or the button, not the word Warning.
 */
function isUnscheduledCaution(item: ReportLine): boolean {
    if (!item.value || item.value === ITEM_STATUS_CODES.warn || item.value === ITEM_STATUS_CODES.inop) {
        return false;
    }
    return shouldCreateIssueForPrompt({
        value: item.value,
        subType: item.subType,
        autoFlag: item.autoFlag,
    });
}

export type ReportStatusTone = 'ok' | 'warn' | 'inop' | 'na';

export type ReportDetailRow = {
    key: string;
    prompt: string;
    section: string;
    description: string;
    statusValue: string;
    statusLabel: string;
    statusTone?: ReportStatusTone;
    notes: string;
    photos: string[];
    sectionGroup: string;
};

export type ReportDetailSection = {
    title: string;
    rows: ReportDetailRow[];
};

export type ReportSummaryCounts = {
    okCount: number;
    warnCount: number;
    inopCount: number;
    naCount: number;
    totalItems: number;
};

export type ReportOverallStatus = {
    tone: ReportStatusTone | 'under_review';
    label: string;
};

function resolveStatusTone(value: string): ReportStatusTone | undefined {
    switch (value) {
        case ITEM_STATUS_CODES.ok:
            return 'ok';
        case ITEM_STATUS_CODES.warn:
        case ITEM_STATUS_CODES.under_review:
        case ITEM_STATUS_CODES.status_unchanged:
            return 'warn';
        case ITEM_STATUS_CODES.inop:
            return 'inop';
        case ITEM_STATUS_CODES.na:
            return 'na';
        default:
            return undefined;
    }
}

export function formatReportStatusLabel(value: string): string {
    switch (value) {
        case ITEM_STATUS_CODES.ok:
            return 'OK';
        case ITEM_STATUS_CODES.warn:
            return 'Warning';
        case ITEM_STATUS_CODES.inop:
            return 'Inoperative';
        case ITEM_STATUS_CODES.na:
            return 'N/A';
        case ITEM_STATUS_CODES.under_review:
        case ITEM_STATUS_CODES.status_unchanged:
            return 'Under Review';
        default:
            return formatOperatorResponseLabel(value) || value;
    }
}

export function computeReportSummaryCounts(items: tItems[]): ReportSummaryCounts {
    let okCount = 0;
    let warnCount = 0;
    let inopCount = 0;
    let naCount = 0;
    let totalItems = 0;

    for (const item of items) {
        if (isReportUMCounterPromptLabel(item.label)) {
            continue;
        }

        if (item.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
            continue;
        }

        totalItems++;

        if (item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions?.length) {
            okCount += item.selectedOptions.length;
            continue;
        }

        if (!item.value) {
            continue;
        }

        switch (item.value) {
            case ITEM_STATUS_CODES.inop:
                inopCount++;
                break;
            case ITEM_STATUS_CODES.warn:
            case ITEM_STATUS_CODES.under_review:
            case ITEM_STATUS_CODES.status_unchanged:
                warnCount++;
                break;
            case ITEM_STATUS_CODES.na:
                naCount++;
                break;
            case ITEM_STATUS_CODES.ok:
                okCount++;
                break;
            default:
                if (isUnscheduledCaution(item)) {
                    warnCount++;
                } else {
                    okCount++;
                }
                break;
        }
    }

    return { okCount, warnCount, inopCount, naCount, totalItems };
}

export function resolveReportOverallStatus(items: tItems[]): ReportOverallStatus {
    const hasInop = items.some((item) => item.value === ITEM_STATUS_CODES.inop);
    const hasWarn = items.some(
        (item) => item.value === ITEM_STATUS_CODES.warn || isUnscheduledCaution(item),
    );
    const hasUnderReview = items.some((item) => item.value === ITEM_STATUS_CODES.under_review);

    if (hasInop) {
        return { tone: 'inop', label: 'Out of Service' };
    }

    if (hasWarn) {
        return { tone: 'warn', label: 'Warning — Review Required' };
    }

    if (hasUnderReview) {
        return { tone: 'under_review', label: 'Under Review' };
    }

    return { tone: 'ok', label: 'OK — No Critical Issues' };
}

export function buildReportDetailSections(items: tItems[]): ReportDetailSection[] {
    const sections: ReportDetailSection[] = [];
    const sectionIndex = new Map<string, ReportDetailSection>();

    items.forEach((item, index) => {
        if (isReportUMCounterPromptLabel(item.label)) {
            return;
        }

        if (item.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
            return;
        }

        const sectionGroup =
            item.type === DEFAULT_FORM_INPUT_TYPES.section && item.title
                ? item.title
                : 'General';

        if (!sectionIndex.has(sectionGroup)) {
            const section = { title: sectionGroup, rows: [] as ReportDetailRow[] };
            sectionIndex.set(sectionGroup, section);
            sections.push(section);
        }

        const photos = item.photos ?? [];
        let statusValue = item.value || '';
        let statusLabel = '';
        let statusTone: ReportStatusTone | undefined;

        if (item.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && item.selectedOptions?.length) {
            statusLabel = item.selectedOptions.join(', ');
            statusValue = statusLabel;
        } else if (statusValue) {
            statusLabel = formatReportStatusLabel(statusValue);
            statusTone = isUnscheduledCaution(item) ? 'warn' : resolveStatusTone(statusValue);
        }

        sectionIndex.get(sectionGroup)!.rows.push({
            key: `${sectionGroup}-${item.label}-${index}`,
            prompt: item.label || 'Item',
            section: item.type === DEFAULT_FORM_INPUT_TYPES.section ? item.title || sectionGroup : sectionGroup,
            description: item.description || '',
            statusValue,
            statusLabel,
            statusTone,
            notes: item.notes || '',
            photos,
            sectionGroup,
        });
    });

    return sections;
}

export function formatLocationSource(type?: string | null): string {
    if (!type) {
        return '—';
    }

    switch (type) {
        case 'gps':
            return 'GPS Detected';
        case 'lastKnownPosition':
            return 'Last Known Position';
        default:
            return type;
    }
}

export function formatReportDateTime(value?: string | null): string {
    if (!value) {
        return '—';
    }

    return new Date(value).toLocaleString();
}
